import { inject, Injectable } from '@angular/core';
import { deleteApp, FirebaseApp, initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import {
    collection,
    Firestore,
    getFirestore,
    onSnapshot,
    query,
    where,
} from 'firebase/firestore';
import {
    catchError,
    defer,
    EMPTY,
    finalize,
    from,
    map,
    mergeMap,
    Observable,
    takeWhile,
    throwError,
    timeout,
} from 'rxjs';
import { AIConvoAskChannel } from '../models/ai-convo-thinking-transport.model';
import { AIConvoThinkingEvent } from '../models/ai-convo-thinking.model';
import { toAIConvoThinkingEvent } from '../models/ai-convo.deserialize';
import { AIConvoService } from './ai-convo.service';

/** Longest wait for the next event: past it the backend may have lost the processing without telling the channel. */
export const AI_CONVO_CHANNEL_TIMEOUT_MS = 120_000;

type OpenedFirestore = { app: FirebaseApp; db: Firestore; userId: number };

@Injectable({ providedIn: 'root' })
export class AIConvoThinkingChannelService {
    private readonly aiConvoService = inject(AIConvoService);
    private appCounter = 1;

    /** Emits the events of one question in `seq` order and completes after the answer or an error. */
    listen(channel: AIConvoAskChannel): Observable<AIConvoThinkingEvent> {
        return defer(() => this.open()).pipe(
            mergeMap(({ app, db, userId }) =>
                this.events(db, channel, userId).pipe(
                    takeWhile(
                        (event) =>
                            event.type !== 'result' && event.type !== 'error',
                        true,
                    ),
                    finalize(() => void deleteApp(app)),
                ),
            ),
        );
    }

    private open(): Observable<OpenedFirestore> {
        return this.aiConvoService.fetchThinkingTransport().pipe(
            mergeMap(({ connection, auth }) => {
                const app = initializeApp(
                    {
                        apiKey: connection.apiKey,
                        authDomain: connection.authDomain,
                        projectId: connection.projectId,
                        messagingSenderId: connection.messagingSenderId,
                    },
                    `aiConvoThinking-${this.appCounter++}-${connection.projectId}`,
                );

                return from(
                    signInWithCustomToken(getAuth(app), auth.token),
                ).pipe(
                    map(({ user }) => ({
                        app,
                        db: getFirestore(app, connection.databaseId),
                        userId: Number(user.uid),
                    })),
                    catchError((error) =>
                        from(deleteApp(app)).pipe(
                            mergeMap(() => throwError(() => error)),
                        ),
                    ),
                );
            }),
        );
    }

    private events(
        db: Firestore,
        { eventsCollectionId, collectionId, documentId }: AIConvoAskChannel,
        userId: number,
    ): Observable<AIConvoThinkingEvent> {
        return new Observable<AIConvoThinkingEvent[]>((subscriber) =>
            onSnapshot(
                query(
                    collection(
                        db,
                        collectionId,
                        documentId,
                        eventsCollectionId,
                    ),
                    // The access rule is evaluated on the shape of the query: the creator constraint is required,
                    // and an orderBy('seq') would need a composite index, so each snapshot is sorted by seq here.
                    where('creatorUserId', '==', userId),
                ),
                (snapshot) =>
                    subscriber.next(
                        snapshot
                            .docChanges()
                            .filter((change) => change.type === 'added')
                            .map((change) => change.doc.data())
                            .sort((a, b) => Number(a['seq']) - Number(b['seq']))
                            .map((data) => toAIConvoThinkingEvent(data))
                            .filter(
                                (event): event is AIConvoThinkingEvent =>
                                    event !== null,
                            ),
                    ),
                (error) => subscriber.error(error),
            ),
        ).pipe(
            // Counted on the raw snapshots: an event the FE does not know still proves the channel is alive.
            timeout(AI_CONVO_CHANNEL_TIMEOUT_MS),
            mergeMap((events) => (events.length ? events : EMPTY)),
        );
    }
}
